import { useLocation } from '@gatsbyjs/reach-router';
import { useTheme } from '@mui/material/styles';
import type { MutableRefObject } from 'react';
import React, { useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import * as ROUTES from '@constants/routes';

import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { useIsMobile } from '@hooks/useIsMobile';

import DrawerSection, { type DrawerItem } from './DrawerSection';
import { SideDrawerStyle } from './SideDrawer.style';

const messages = defineMessages({
  more: {
    id: 'moreDrawer.more',
    defaultMessage: 'More',
  },
  insurance: {
    id: 'moreDrawer.insurance',
    defaultMessage: 'Insurance',
  },
  legal: {
    id: 'moreDrawer.legal',
    defaultMessage: 'Legal/Directives',
  },
  emergencyAccess: {
    id: 'moreDrawer.emergencyAccess',
    defaultMessage: 'Emergency Access',
  },
  uploads: {
    id: 'moreDrawer.uploads',
    defaultMessage: 'Records/Documents',
  },
  connections: {
    id: 'moreDrawer.connections',
    defaultMessage: 'Hospital/Clinic Connections',
  },
  auditLogs: {
    id: 'moreDrawer.auditLogs',
    defaultMessage: 'Audit Logs',
  },
});

function MoreDrawer({
  isOpen = false,
  onClose,
  btnRef = undefined,
}: {
  isOpen?: boolean;
  onClose?: (event: MouseEvent) => void;
  btnRef?: MutableRefObject<HTMLButtonElement | null>;
}) {
  const { formatMessage } = useIntl();
  const {
    palette: { secondary },
  } = useTheme();
  const { flags } = useFeatureFlags();
  const drawerRef = useRef<HTMLInputElement>(null);
  const mobile = useIsMobile();
  const location = useLocation();

  const pathname = location.pathname;

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (
        isOpen &&
        onClose &&
        !drawerRef.current?.contains(event.target as Node) &&
        !btnRef?.current?.contains(event.target as Node)
      ) {
        onClose(event);
      }
    }

    document.addEventListener('mousedown', handleClick, false);
    return () => document.removeEventListener('mousedown', handleClick, false);
  }, [onClose, isOpen, btnRef]);

  const drawerItems: DrawerItem[] = [
    {
      icon: { color: secondary.main, name: 'address-card' },
      text: formatMessage(messages.insurance),
      isSelected: pathname.includes(ROUTES.INSURANCE),
      to: ROUTES.INSURANCE,
    },
    {
      icon: { color: secondary.main, name: 'gavel' },
      text: formatMessage(messages.legal),
      isSelected: pathname.includes(ROUTES.LEGAL),
      to: ROUTES.LEGAL,
    },
    {
      icon: { color: secondary.main, name: 'display-medical' },
      text: formatMessage(messages.emergencyAccess),
      isSelected: pathname.includes(ROUTES.EMERGENCY_ACCESS_SETTINGS),
      to: ROUTES.EMERGENCY_ACCESS_SETTINGS,
    },
    {
      icon: { color: secondary.main, name: 'files' },
      text: formatMessage(messages.uploads),
      isSelected: pathname.includes(ROUTES.UPLOADS),
      to: ROUTES.UPLOADS,
    },
    {
      icon: { color: secondary.main, name: 'hospital' },
      text: formatMessage(messages.connections),
      isSelected: pathname.includes(ROUTES.HOSPITAL_CONNECTIONS),
      to: ROUTES.HOSPITAL_CONNECTIONS,
    },
  ];
  if (flags.isAuditLogsListAuditLogsEnabled) {
    drawerItems.push({
      icon: { color: secondary.main, name: 'history' },
      text: formatMessage(messages.auditLogs),
      isSelected: pathname.includes(ROUTES.AUDIT_LOGS),
      to: ROUTES.AUDIT_LOGS,
    });
  }
  return (
    <SideDrawerStyle
      className={`${!isOpen && 'hidden'} ${mobile && 'mobile'}`}
      ref={drawerRef}
    >
      <DrawerSection title={formatMessage(messages.more)} items={drawerItems} />
    </SideDrawerStyle>
  );
}

export default MoreDrawer;
